import { FC, useState, Fragment } from 'react';
import { push } from 'connected-react-router';
import config from 'config';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { GroupsAsync } from 'store/group/groupAsync';
import { selectCurrentUser } from 'store/currentUser/currentUserSelectors';
import { IGroup } from 'models/Group';
import UserRoles from 'types/UserRoles';
// Dialogs
import ConfirmationDialog from 'dialogs/Confirmation.dialog';
import GroupQuizzesDialog from 'dialogs/GroupQuizzesDialog';
// Components
import { IconButton } from 'components/Buttons';
import { Tooltip } from 'components/Tooltip';
// Icons
import { FaEdit, FaTrash, FaListAlt, FaPoll, FaRegUser, FaChartBar } from 'react-icons/fa';
// i18next
import { useTranslation } from 'react-i18next';

type Props = {
  group: IGroup;
}

const GroupsListItem:FC<Props> = ({ group }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currentUser = useAppSelector(selectCurrentUser);

  const [ isLoading, setIsLoading ] = useState(false);
  const [ dialogConfirmationOpen, setDialogConfirmationOpen ] = useState<boolean>(false);
  const [ dialogGroupQuizzesOpen, setDialogGroupQuizzesOpen ] = useState<boolean>(false);

  const toggleConfirmationDialog = () => setDialogConfirmationOpen(!dialogConfirmationOpen);
  const toggleGroupQuizzesDialog = () => setDialogGroupQuizzesOpen(!dialogGroupQuizzesOpen);

  const handleGoTo = (url:string) => dispatch(push(url));

  const handleLiveLeaderBoard = () => {
    window.open(`${config.quizURL}/quiz-group/${group.slug}/live-leaderboard`);
  };

  const handleDelete = () => {
    setIsLoading(true);
    dispatch(GroupsAsync.deleteGroup(group._id)).unwrap()
      .finally(() => setIsLoading(false));
  }

  const isAdmin = currentUser?.groups.includes(UserRoles.Admin);
  const role = isAdmin ? 'admin' : 'owner';

  return (
    <Fragment>
      <tr>
        <td className="text-left title">{group.name}</td>
        <td className="text-left text">
          <FaRegUser className="icon" size="16px" />
          {(group as any).createdByName || '-'}
        </td>
        <td className="text-right actions">
          <Tooltip title={t('pages.groups.editButton')}>
            <IconButton
              onClick={() => handleGoTo(`/${role}/groups/${group._id}`)}
            ><FaEdit /></IconButton>
          </Tooltip>
          <Tooltip className="ml-1" title={t('pages.groups.deleteButton')}>
            <IconButton
              onClick={toggleConfirmationDialog}
            ><FaTrash /></IconButton>
          </Tooltip>
          <Tooltip className="ml-1" title={t('labels.quizzes')}>
            <IconButton
              onClick={toggleGroupQuizzesDialog}
            ><FaListAlt /></IconButton>
          </Tooltip>
          <Tooltip className="ml-1" title={group.publicLeaderboard.enabled ? t('labels.liveLeaderboard') : ''}>
            <IconButton
              disabled={!group.publicLeaderboard.enabled}
              onClick={handleLiveLeaderBoard}
            ><FaChartBar /></IconButton>
          </Tooltip>
          <Tooltip className="ml-1" title={t('labels.report')}>
            <IconButton
              onClick={() => handleGoTo(`/owner/groups/${group._id}/report`)}
            ><FaPoll /></IconButton>
          </Tooltip>
        </td>
      </tr>
      {dialogConfirmationOpen ? (
        <ConfirmationDialog
          title={t('pages.groups.deleteTitle', { name: group.name })}
          content={t('pages.groups.deleteConfirmation')}
          open={dialogConfirmationOpen}
          loading={isLoading}
          onClose={toggleConfirmationDialog}
          onConfirm={handleDelete}
        />
      ) : null}
      {dialogGroupQuizzesOpen ? (
        <GroupQuizzesDialog
          groupId={group._id}
          groupSlug={group.slug}
          open={dialogGroupQuizzesOpen}
          onClose={toggleGroupQuizzesDialog}
        />
      ) : null}
    </Fragment>
  )
}

export default GroupsListItem;
